import { type ReactNode, useEffect } from "react";
import { GroupRrspOfferField } from "@/components/GroupRrspOfferField";
import { JobStartField } from "@/components/JobStartField";
import { groupRrspChoiceNeeded } from "@/questionnaire/groupRrspLink";
import { DebtRow, PropertyRow } from "@/components/OwedRows";
import {
  Choice,
  FieldError,
  InfoTip,
  MoneyField,
  SelectField,
  TextField,
  YesNo,
} from "@/components/fields";
import { FirstTimeBuyerChecks } from "@/components/FirstTimeBuyer";
import { PensionType } from "@/generated/core/v1/person";
import type { FieldError as EngineFieldError } from "@/generated/core/v1/value_source";
import { money } from "@/plan-logic/lib/format";
import {
  type DraftIncome,
  type DraftPension,
  type DraftProperty,
  INCOME_TYPE_HINT,
  INCOME_TYPE_LABEL,
  incomeType,
  isJobKind,
  kindForType,
  MATCHABLE_KINDS,
  members,
  newDebt,
  newIncome,
  newPension,
  newProperty,
  pensionJob,
  personLabel,
} from "@/plan-logic/questionnaire/draft";
import { jobStartQuestion } from "@/plan-logic/questionnaire/jobStart";
import { Screen } from "../Screen";
import { removeAt, updateAt, useDraft } from "../useDraft";

const SUB = { color: "var(--ink-3)", fontSize: 13.5, margin: "4px 0 14px" } as const;

/** The workplace pension kinds (v39 `PEN_KINDS`, with their tips). */
const PENSION_KINDS: [PensionType, string, string, string, ReactNode][] = [
  [
    PensionType.DEFINED_BENEFIT,
    "🏛️",
    "Defined benefit",
    "A set monthly income for life once you retire.",
    <>
      <b>Defined benefit</b>Your employer promises you a set monthly income for life, usually worked
      out from your salary and your years of service. The employer carries the investment risk, not
      you. Common in government, teaching, healthcare and unions.
    </>,
  ],
  [
    PensionType.DEFINED_CONTRIBUTION,
    "📈",
    "Defined contribution",
    "A pot of money that grows until you draw on it.",
    <>
      <b>Defined contribution</b>You and usually your employer both put money in and it gets
      invested. What you end up with depends on what went in and how it grew, so you carry the
      investment risk. Group RRSPs and DPSPs work the same way.
    </>,
  ],
];

/**
 * What everyone earns, before tax (v40/v44): a column per adult (the partner's only with a partner),
 * each income named, of one of four types, with "Add another income" under each column; then a
 * workplace pension per adult, attached to one of their jobs.
 */
export function IncomeStep({ eyebrow }: { eyebrow: string }) {
  const { draft, patch, err } = useDraft();
  const people = members(draft);

  // One empty income row per adult to start with.
  useEffect(() => {
    const missing = people.filter((p) => !draft.incomes.some((i) => i.memberUuid === p.uuid));
    if (missing.length)
      patch("incomes", [...draft.incomes, ...missing.map((p) => newIncome(p.uuid))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // asked only when the engine can't link them itself; accounts another job uses aren't offered
  const matchAccountChoice = (member: string, job: string): [string, string][] | undefined => {
    const accounts = draft.accounts.filter((a) => a.kind === "grouprrsp" && a.ownerUuid === member);
    const matched = draft.incomes.filter((i) => i.memberUuid === member && i.groupRrspPlan);
    const taken = new Set(
      matched.filter((i) => i.uuid !== job).map((i) => i.groupRrspPlan!.groupRrspUuid),
    );
    return groupRrspChoiceNeeded(accounts.length, matched.length)
      ? accounts
          .filter((a) => !taken.has(a.uuid))
          .map((a) => [a.uuid, a.name || `Group RRSP (${money(a.balance)})`])
      : undefined;
  };
  const setIncome = (i: number, change: Partial<DraftIncome>) =>
    patch(
      "incomes",
      updateAt(draft.incomes, i, (x) => ({ ...x, ...change })),
    );
  const setPension = (uuid: string, change: Partial<DraftPension>) =>
    patch("pensions", {
      ...draft.pensions,
      [uuid]: { ...(draft.pensions[uuid] ?? newPension()), ...change },
    });

  return (
    <Screen eyebrow={eyebrow} h1="onboarding.gaps7.h1" sub="onboarding.gaps7.sub">
      <div className="card pad">
        <h2>What you earn before tax</h2>
        <p style={SUB}>
          Name each source whatever you call it &mdash; the day job, the side business, the rental.
          One line each. If someone in the household isn&rsquo;t earning right now, leave their
          amount blank.
        </p>
        <div className="pplcols">
          {people.map((p, pi) => {
            const name = personLabel(p, pi ? "Your partner" : "You");
            const rows = draft.incomes
              .map((inc, i) => ({ inc, i }))
              .filter(({ inc }) => inc.memberUuid === p.uuid);
            return (
              <div className="pplcol" key={p.uuid} data-testid={`income-col-${pi}`}>
                <div className="pplname">{name}</div>
                {rows.map(({ inc, i }, n) => {
                  const type = incomeType(inc.kind);
                  return (
                    <div className="rowbox" key={inc.uuid} data-testid={`income-${pi}-${n}`}>
                      <div className="rowhead">
                        <b>Income {n + 1}</b>
                        {n > 0 && (
                          <button
                            type="button"
                            className="rm"
                            onClick={() => patch("incomes", removeAt(draft.incomes, i))}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="stackf">
                        <TextField
                          label="Name of income source"
                          value={inc.name ?? ""}
                          placeholder={n === 0 ? "Day job" : "Side business, rental, pension…"}
                          onChange={(v) => setIncome(i, { name: v })}
                          testId={`income-${pi}-${n}-name`}
                          style={{ margin: 0 }}
                        />
                        <SelectField
                          label="What kind"
                          value={type}
                          options={INCOME_TYPE_LABEL}
                          onChange={(t) => {
                            const kind = kindForType(t, inc.kind);
                            setIncome(i, {
                              kind,
                              groupRrspPlan: MATCHABLE_KINDS.has(kind)
                                ? inc.groupRrspPlan
                                : undefined,
                            });
                          }}
                          hint={INCOME_TYPE_HINT[type]}
                          testId={`income-${pi}-${n}-type`}
                          style={{ margin: 0 }}
                        />
                        <MoneyField
                          label="Amount before tax, a year"
                          value={inc.amount}
                          onChange={(amount) => setIncome(i, { amount })}
                          error={err(`incomes.${i}.amount`)}
                          testId={`income-${pi}-${n}-amount`}
                          style={{ margin: 0 }}
                        />
                      </div>
                      {MATCHABLE_KINDS.has(inc.kind) && (
                        <GroupRrspOfferField
                          offer={inc.groupRrspPlan}
                          salary={inc.amount}
                          onChange={(groupRrspPlan) => setIncome(i, { groupRrspPlan })}
                          testId={`income-${pi}-${n}-match`}
                          accounts={matchAccountChoice(p.uuid, inc.uuid)}
                        />
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="mini"
                  onClick={() => patch("incomes", [...draft.incomes, newIncome(p.uuid)])}
                  data-testid={`income-add-${pi}`}
                >
                  + Add another income
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card pad mt">
        <h2>A workplace pension?</h2>
        <p style={SUB}>
          If you have one it is often the biggest single thing in a retirement plan.
        </p>
        <div className="pplcols" data-testid="pen-people">
          {people.map((p, pi) => {
            const pn = draft.pensions[p.uuid];
            const jobs = draft.incomes
              .filter((inc) => inc.memberUuid === p.uuid && isJobKind(inc.kind))
              .map((inc): [string, string] => [
                inc.uuid,
                inc.name?.trim() ||
                  `Income ${draft.incomes.filter((x) => x.memberUuid === p.uuid).indexOf(inc) + 1}`,
              ]);
            const jobUuid = jobs.some(([u]) => u === pn?.jobUuid)
              ? pn!.jobUuid
              : (jobs[0]?.[0] ?? "");
            const job = pensionJob(draft, p.uuid);
            return (
              <div className="pplcol" key={p.uuid}>
                <div className="pplname">{personLabel(p, pi ? "Your partner" : "You")}</div>
                <div className="rowbox" data-testid={`pension-${pi}`}>
                  <div className="gateq">
                    <span className="gql">Is there a workplace pension?</span>
                    <YesNo
                      value={!!pn?.has}
                      onChange={(has) => setPension(p.uuid, { has, jobUuid })}
                      label="Is there a workplace pension?"
                      testId={`pension-${pi}-has`}
                    />
                  </div>
                  {pn?.has && (
                    <>
                      <div className="choices" style={{ marginTop: 12 }}>
                        {PENSION_KINDS.map(([type, icon, title, line, tip]) => (
                          <Choice
                            key={type}
                            selected={pn.type === type}
                            onClick={() => setPension(p.uuid, { type })}
                            icon={icon}
                            title={
                              <>
                                {title}
                                <InfoTip label={`What a ${title.toLowerCase()} pension is`}>
                                  {tip}
                                </InfoTip>
                              </>
                            }
                            testId={`pension-${pi}-${type === PensionType.DEFINED_BENEFIT ? "db" : "dc"}`}
                          >
                            {line}
                          </Choice>
                        ))}
                      </div>
                      {jobs.length ? (
                        <>
                          <SelectField
                            label="Which job or income is it attached to?"
                            value={jobUuid}
                            options={jobs}
                            onChange={(v) => setPension(p.uuid, { jobUuid: v })}
                            hint="A defined benefit plan is worked out from the salary it sits against, so Abi needs to know which one."
                            testId={`pension-${pi}-job`}
                            style={{ margin: "14px 0 0" }}
                          />
                          {pn.type === PensionType.DEFINED_BENEFIT && job && (
                            <JobStartField
                              question={jobStartQuestion(
                                pi ? p.givenName.trim() || "your partner" : "you",
                                job.name ?? "",
                              )}
                              value={job.start ?? {}}
                              onChange={(start) => setIncome(draft.incomes.indexOf(job), { start })}
                              error={err(`incomes.${draft.incomes.indexOf(job)}.start`)}
                              testId={`pension-${pi}-start`}
                              style={{ marginTop: 14 }}
                            />
                          )}
                        </>
                      ) : (
                        <p className="hint" style={{ marginTop: 12 }}>
                          Add the job it comes with above (employment or self-employment), so Abi
                          knows which salary it sits against.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Screen>
  );
}

/**
 * What you owe: properties (each with its own mortgage question), whether each adult is a first-time
 * home buyer (asked whether or not they own property), and other debts.
 */
export function DebtsStep({ eyebrow }: { eyebrow: string }) {
  const { draft, patch, err } = useDraft();
  const cards = draft.accounts.filter((a) => a.kind === "credit");
  const setProp = (i: number, change: Partial<DraftProperty>) =>
    patch(
      "properties",
      updateAt(draft.properties, i, (x) => ({ ...x, ...change })),
    );
  return (
    <Screen eyebrow={eyebrow} h1="onboarding.gaps8.h1" sub="onboarding.gaps8.sub">
      <div className="card pad mt">
        <h2>Do you own any property?</h2>
        <p style={SUB}>
          A home you own outright still matters here &mdash; it is usually the biggest thing on your
          side of the ledger. We ask about the mortgage separately, on each property. Renting your
          home but own a cottage or a rental? Add it and pick what it is: your rent stays in the
          plan.
        </p>
        <YesNo
          value={draft.hasProperty}
          onChange={(v) => {
            patch("hasProperty", v);
            if (v && !draft.properties.length) patch("properties", [newProperty(true)]);
          }}
          testId="has-property"
        />
        {draft.hasProperty && (
          <div style={{ marginTop: 16 }}>
            <p style={{ ...SUB, margin: "0 0 14px" }}>
              Where there is a mortgage, the figures come from your last statement or your
              lender&rsquo;s site. The rate decides whether paying it down beats investing the same
              dollar: the info icon beside it shows where to find yours. Not sure? Leave it blank
              and Abi estimates it until you fill it in.
            </p>
            {draft.properties.map((p, i) => (
              <PropertyRow
                key={p.uuid}
                row={p}
                i={i}
                onChange={(change) => setProp(i, change)}
                onRemove={
                  i > 0 ? () => patch("properties", removeAt(draft.properties, i)) : undefined
                }
                err={err}
              />
            ))}
            <FieldError error={err("properties")} />
            <button
              type="button"
              className="mini"
              onClick={() => patch("properties", [...draft.properties, newProperty(false)])}
            >
              + Add another property
            </button>
          </div>
        )}
        <FirstTimeBuyerChecks
          adults={members(draft)}
          value={draft.firstTimeBuyer}
          onChange={(uuid, v) => patch("firstTimeBuyer", { ...draft.firstTimeBuyer, [uuid]: v })}
        />
      </div>
      <div className="card pad mt">
        <h2>Do you have any other debts?</h2>
        <p style={SUB}>
          Lines of credit, car loans, student loans, money owed to the CRA.
          {cards.length
            ? ` The ${cards.length === 1 ? "credit card" : `${cards.length} credit cards`} you added with your accounts ${cards.length === 1 ? "is" : "are"} already counted.`
            : ""}{" "}
          The rate decides the order Abi pays them off in. Not sure of one? Leave it blank and Abi
          estimates it until you fill it in.
        </p>
        <YesNo
          value={draft.hasDebts}
          onChange={(v) => {
            patch("hasDebts", v);
            if (v && !draft.debts.length) patch("debts", [newDebt()]);
          }}
          testId="has-debts"
        />
        {draft.hasDebts && (
          <div style={{ marginTop: 16 }}>
            {draft.debts.map((x, i) => (
              <DebtRow
                key={x.uuid}
                row={x}
                i={i}
                onChange={(change) =>
                  patch(
                    "debts",
                    updateAt(draft.debts, i, (d) => ({ ...d, ...change })),
                  )
                }
                onRemove={i > 0 ? () => patch("debts", removeAt(draft.debts, i)) : undefined}
                err={err}
              />
            ))}
            <FieldError error={err("debts")} />
            <button
              type="button"
              className="mini"
              onClick={() => patch("debts", [...draft.debts, newDebt()])}
            >
              + Add another debt
            </button>
          </div>
        )}
      </div>
    </Screen>
  );
}

/**
 * Your safety net (the rainy-day fund), the last step (v39: the pension moved under income, and the
 * retirement step is gone): "Build my plan" saves the household here.
 */
export function SafetyStep({
  eyebrow,
  onBuild,
  building,
  serverErrors,
}: {
  eyebrow: string;
  onBuild: () => void;
  building: boolean;
  serverErrors: EngineFieldError[];
}) {
  const { draft, patch, err } = useDraft();
  const cash = draft.accounts.filter((a) => a.kind === "chequing" || a.kind === "savings");
  const people = members(draft);
  const owner = (uuid: string) => {
    const i = people.findIndex((p) => p.uuid === uuid);
    return i < 0 ? "" : personLabel(people[i], i ? "Your partner" : "You");
  };
  const none = !draft.emergency.length;
  const toggle = (uuid: string) => {
    const on = draft.emergency.some((e) => e.accountUuid === uuid);
    patch(
      "emergency",
      on
        ? draft.emergency.filter((e) => e.accountUuid !== uuid)
        : [...draft.emergency, { accountUuid: uuid, whole: true }],
    );
  };
  return (
    <Screen
      eyebrow={eyebrow}
      h1="onboarding.gaps9.h1"
      sub="onboarding.gaps9.sub"
      nextLabel={building ? "Saving…" : "Build my plan"}
      onNext={onBuild}
      nextDisabled={building}
      nextTestId="onb-build"
    >
      <div className="card pad mt">
        <h2>Which of this money is your rainy-day fund?</h2>
        <p style={SUB}>
          Only you know what it&rsquo;s for. Money you&rsquo;ve set aside on purpose is treated
          differently from money that&rsquo;s about to be spent, so the plan won&rsquo;t try to
          invest it or throw it at your card.
        </p>
        <div id="ef-list">
          <button
            type="button"
            className={`efrow none${none ? " sel" : ""}`}
            onClick={() => patch("emergency", [])}
            data-testid="ef-none"
          >
            <span className="efck" />
            <span style={{ flex: 1 }}>
              <b>None of it</b>
              <span className="eft">
                We don&rsquo;t have anything set aside for emergencies yet.
              </span>
            </span>
          </button>
          {cash.map((a) => {
            const idx = draft.emergency.findIndex((e) => e.accountUuid === a.uuid);
            const e = draft.emergency[idx];
            const label = a.name || (a.kind === "savings" ? "Savings" : "Chequing");
            return (
              <div className="efwrap" key={a.uuid}>
                <button
                  type="button"
                  className={`efrow${e ? " sel" : ""}`}
                  onClick={() => toggle(a.uuid)}
                  data-testid={`ef-${a.uuid}`}
                >
                  <span className="efck" />
                  <span style={{ flex: 1 }}>
                    <b>{label}</b>
                    <span className="eft">
                      {owner(a.ownerUuid)} &middot; {money(a.balance ?? 0)} today
                    </span>
                  </span>
                </button>
                {e && (
                  <div className="efamt">
                    <button
                      type="button"
                      className={`ynb${e.whole ? " sel" : ""}`}
                      onClick={() =>
                        patch(
                          "emergency",
                          updateAt(draft.emergency, idx, (x) => ({ ...x, whole: true })),
                        )
                      }
                    >
                      All of it
                    </button>
                    <button
                      type="button"
                      className={`ynb${!e.whole ? " sel" : ""}`}
                      onClick={() =>
                        patch(
                          "emergency",
                          updateAt(draft.emergency, idx, (x) => ({ ...x, whole: false })),
                        )
                      }
                    >
                      Just part of it
                    </button>
                    {!e.whole && (
                      <MoneyField
                        label="How much"
                        value={e.amount}
                        onChange={(amount) =>
                          patch(
                            "emergency",
                            updateAt(draft.emergency, idx, (x) => ({ ...x, amount })),
                          )
                        }
                        error={err(`emergency.${idx}.amount`)}
                        ariaLabel={`Amount set aside in ${label}`}
                        style={{ margin: "8px 0 0", flexBasis: "100%" }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="hint" style={{ marginTop: 12 }}>
          Nothing set aside yet? Say so. Building one is usually the first thing we&rsquo;d tell you
          to do.
        </p>
      </div>
      {serverErrors.length > 0 && (
        <div className="card pad mt" role="alert" data-testid="onb-server-errors">
          <h2>A few answers need another look</h2>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
            {serverErrors.map((e) => (
              <li key={`${e.path}:${e.message}`} style={{ fontSize: 14 }}>
                {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Screen>
  );
}
